<script lang="ts">
  import type { Snippet } from 'svelte';
  interface Props {
    variant?: 'primary' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    href?: string;
    type?: 'button' | 'submit' | 'reset';
    onclick?: (e: MouseEvent) => void;
    disabled?: boolean;
    fullWidth?: boolean;
    children?: Snippet;
    'aria-label'?: string;
  }
  let {
    variant = 'primary',
    size = 'md',
    href,
    type = 'button',
    onclick,
    disabled,
    fullWidth = false,
    children,
    'aria-label': ariaLabel
  }: Props = $props();
</script>

{#if href}
  <a class="btn btn-{variant} size-{size}" class:full={fullWidth} {href} aria-label={ariaLabel}>
    {@render children?.()}
  </a>
{:else}
  <button
    class="btn btn-{variant} size-{size}"
    class:full={fullWidth}
    {type}
    {onclick}
    {disabled}
    aria-label={ariaLabel}
  >
    {@render children?.()}
  </button>
{/if}

<style>
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 18px;
    font-size: 14px;
    font-weight: 600;
    border-radius: var(--radius-sm);
    cursor: pointer;
    text-decoration: none;
    transition:
      transform var(--t-fast),
      background var(--t),
      border-color var(--t),
      box-shadow var(--t);
    border: 1px solid transparent;
    white-space: nowrap;
    font-family: inherit;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .btn.full {
    width: 100%;
  }
  .size-sm {
    padding: 8px 14px;
    font-size: 13px;
  }
  .size-lg {
    padding: 14px 24px;
    font-size: 15px;
  }
  .btn-primary {
    background: var(--accent);
    color: var(--bg);
  }
  .btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px var(--accent-glow);
  }
  :global([data-theme='brutalist']) .btn-primary {
    background: var(--text);
    color: var(--bg);
    border: 2px solid var(--text);
    box-shadow: 4px 4px 0 var(--text);
    font-weight: 800;
  }
  :global([data-theme='brutalist']) .btn-primary:hover:not(:disabled) {
    background: var(--accent-dim);
    color: var(--text);
    transform: translate(-2px, -2px);
    box-shadow: 6px 6px 0 var(--text);
  }
  :global([data-theme='brutalist']) .btn-primary:active:not(:disabled) {
    transform: translate(2px, 2px);
    box-shadow: 1px 1px 0 var(--text);
  }
  .btn-ghost {
    background: transparent;
    color: var(--text);
    border: 1px solid var(--border-2);
  }
  .btn-ghost:hover:not(:disabled) {
    background: var(--bg-card);
    border-color: var(--border-strong);
  }
  :global([data-theme='brutalist']) .btn-ghost {
    border: 2px solid var(--text);
    font-weight: 800;
    box-shadow: 4px 4px 0 var(--text);
  }
  :global([data-theme='brutalist']) .btn-ghost:hover:not(:disabled) {
    background: var(--accent-dim);
    transform: translate(-2px, -2px);
    box-shadow: 6px 6px 0 var(--text);
  }
  :global([data-theme='brutalist']) .btn-ghost:active:not(:disabled) {
    transform: translate(2px, 2px);
    box-shadow: 1px 1px 0 var(--text);
  }
</style>
